import create from "zustand";

import {
    fetchCategories as apiFetchCategories,
    fetchProduct as apiFetchProduct,
    fetchProducts as apiFetchProducts,
    fetchRelatedProducts as apiFetchRelatedProducts,
} from "./api";
import {
    iShopwareCartListPrice,
    iShopwareCartPriceReference,
    iShopwareCartPriceRegulation,
    iShopwareCartTaxCalculated,
    iShopwareCartTaxRule,
} from "./ShoppingCart/store";

/**
 * Use the Shopware apiAlias field from the resource in camelcase to define the shopware interfaces using:
 * iShopware{apiAlias}
 */
export interface iShopwareProductListing {
    elements: iShopwareProduct[];
    entity: string;
    aggregations: any[];
    page: number;
    limit: number;
    sorting: string;
    currentFilters: any[];
    availableSortings: any[];
    streamId: any;
    apiAlias: string;
}

export interface iShopwareDeliveryTime {
    id: string;
    name: string;
    min: number;
    max: number;
    unit: string;
    apiAlias: string;
}

export interface iShopwareProduct {
    id: string;
    categoryIds: Array<string>;
    ean: string;
    name: string;
    active: boolean;
    available: boolean;
    deliveryTime: null | iShopwareDeliveryTime;
    description: string;
    width: number;
    height: number;
    length: number;
    maxPurchase: number;
    minPurchase: number;
    cover: iShopwareProductMedia;
    media: iShopwareProductMedia[];
    calculatedCheapestPrice: iShopwareProductCalculatedCheapestPrice;
    productNumber: string;
    shippingFree: boolean;
    stock: number;
    availableStock: number;
    weight: number;
    apiAlias: string;
    metaDescription?: string;
    metaTitle?: string;
}

export interface iShopwareProductMedia {
    id: string;
    media: iShopwareMedia;
    position: number;
    createdAt: string;
    updatedAt: string;
    apiAlias: string;
}

export interface iShopwareMedia {
    id: string;
    alt?: string;
    filename: string;
    url: string;
    thumbnails: iShopwareMediaThumbnail[];
    createdAt: string;
    updatedAt: string;
    apiAlias: string;
}

export interface iShopwareMediaThumbnail {
    id: string;
    width: number;
    height: number;
    url: string;
    createdAt: string;
    updatedAt: string;
    apiAlias: string;
}

export interface iShopwareProductCalculatedCheapestPrice {
    calculatedTaxes: Array<iShopwareCartTaxCalculated>;
    hasRange: boolean;
    listPrice: null | iShopwareCartListPrice;
    quantity: number;
    referencePrice: iShopwareCartPriceReference;
    regulationPrice: null | iShopwareCartPriceRegulation;
    taxRules: Array<iShopwareCartTaxRule>;
    totalPrice: number;
    unitPrice: number;
    apiAlias: string;
}

export interface iShopwareCategory {
    id: string;
    parentId?: string;
    name: string;
    description?: string;
    products?: Array<iShopwareProduct> | null;
    breadcrumb: Array<string>;
    active: boolean;
    visible: boolean;
    createdAt: string;
    updatedAt: string;
    apiAlias: string;
}

export interface iShopwareSort {
    key: string;
    label: string;
}

export interface iShop {
    categories: Array<iShopwareCategory>;
    selectedCategory: undefined | string;
    isShippingInfoModalVisible: boolean;
    isLoadingProducts: boolean;
    isLoadingCategories: boolean;
    isLoadingRelatedProducts: boolean;
    search: string;
    sort: iShopwareSort;
    availableSortings: iShopwareSort[];
    currentSort: string;
    searchedProducts: iShopwareProduct[] | [];
    shopwareMainCategoryId: string | undefined;
    setCategories(this: void, categories: Array<iShopwareCategory>): void;
    setSelectedCategory(this: void, selectedCategory: undefined | string): void;
    getAllProducts(this: void): Array<iShopwareProduct>;
    getProduct(this: void, productId: string): iShopwareProduct | undefined;
    fetchProduct(
        this: void,
        productId: string
    ): Promise<iShopwareProduct | undefined>;
    fetchRelatedProducts(
        this: void,
        productId: string
    ): Promise<iShopwareProduct[]>;
    fetchCategories(
        this: void,
        mainCategoryId: string
    ): Promise<iShopwareCategory[]>;
    fetchProductsByCategory(
        this: void,
        categoryId: string
    ): Promise<iShopwareProduct[] | undefined>;
    fetchProductsBySearch(
        this: void,
        categoryId: string
    ): Promise<iShopwareProduct[]>;
    setIsShippingInfoModalVisible(this: void, state: boolean): void;
    setSearch(value: string): void;
    setSort(value: iShopwareSort): void;
}

const useStore = create<iShop>((set, get) => ({
    categories: [],
    isLoadingProducts: false,
    isLoadingCategories: false,
    isLoadingRelatedProducts: false,
    selectedCategory: undefined,
    isShippingInfoModalVisible: false,
    search: "",
    sort: {
        key: "",
        label: "",
    },
    currentSort: "",
    availableSortings: [],
    searchedProducts: [],
    shopwareMainCategoryId: undefined,
    setCategories: (categories: Array<iShopwareCategory>) =>
        set({ categories }),
    setSelectedCategory: (selectedCategory) => set({ selectedCategory }),
    getAllProducts: () => {
        const { categories } = get();
        let products: Array<iShopwareProduct> = [];

        categories.forEach((category) => {
            if (category.products && category.products.length > 0) {
                products = [...products, ...category.products];
            }
        });

        return products;
    },
    getProduct: (productId: string): iShopwareProduct | undefined => {
        const { getAllProducts } = get();
        const products = getAllProducts();

        return products.find((product) => product.id === productId);
    },
    setIsShippingInfoModalVisible: (state) => {
        set({ isShippingInfoModalVisible: state });
    },
    fetchProduct: async (productId) => {
        try {
            set({ isLoadingProducts: true });
            return await apiFetchProduct(productId);
        } catch (error) {
            return undefined;
        } finally {
            set({ isLoadingProducts: false });
        }
    },
    fetchRelatedProducts: async (productId) => {
        try {
            set({ isLoadingRelatedProducts: true });
            return await apiFetchRelatedProducts(productId);
        } catch (error) {
            return [];
        } finally {
            set({ isLoadingRelatedProducts: false });
        }
    },
    fetchCategories: async (mainCategoryId) => {
        const { setCategories } = get();
        set({
            isLoadingCategories: true,
            shopwareMainCategoryId: mainCategoryId,
        });
        const categories = await apiFetchCategories(mainCategoryId);
        if (categories.length > 0) {
            setCategories(categories);
            set({ isLoadingCategories: false });
            return categories;
        }
        set({ isLoadingCategories: false });
        return [];
    },
    fetchProductsByCategory: async (categoryId) => {
        const { categories, sort } = get();
        set({ isLoadingProducts: true });
        const products = await apiFetchProducts(categoryId, sort);
        if (products.elements.length > 0) {
            //Apply Products to its category:
            const categoriesWithProducts = categories.map((category) => ({
                ...category,
                products:
                    category.id === categoryId
                        ? products.elements
                        : category.products,
            }));
            set({ categories: categoriesWithProducts });
            const sortedAvailableSortings = [
                ...products.availableSortings,
            ].sort((a, b) => a.priority - b.priority);
            set({
                isLoadingProducts: false,
                availableSortings: sortedAvailableSortings,
                currentSort: products.sorting,
                sort: sortedAvailableSortings.find(
                    (item) => item.key === products.sorting
                ),
            });
            return products.elements;
        }
        set({ isLoadingProducts: false });
        return [];
    },

    fetchProductsBySearch: async (categoryId) => {
        const { sort, search } = get();
        set({ isLoadingProducts: true, selectedCategory: undefined });
        const products = await apiFetchProducts(categoryId, sort, search);

        set({ searchedProducts: products.elements });
        const sortedAvailableSortings = [...products.availableSortings].sort(
            (a, b) => a.priority - b.priority
        );
        set({
            availableSortings: sortedAvailableSortings,
            currentSort: products.sorting,
            sort: sortedAvailableSortings.find(
                (item) => item.key === products.sorting
            ),
        });
        set({ isLoadingProducts: false });
        return products.elements;
    },

    setSearch: (value) => {
        set({ search: value });
    },

    setSort: (value: iShopwareSort) => {
        set({ sort: value });
    },
}));

export default useStore;
